import React, { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import Modal from '../../common/Modal';
import FormControl from '../../common/FormControl';
import Button from '../../common/Button';
import Toast, { ToastMessage } from '../../common/Toast';
import { capaImpactsService, CapaImpact, ImpactUpdate, ImpactSeverity } from '../../../services/capaImpactsService';
import { useAuth } from '../../../contexts/AuthContext';
import extractApiError from '../../../utils/apiError';
import { useFieldValidation } from '../../../validation/useFieldValidation';
import { ValidatedTextarea } from '../../../validation/ValidatedFields';
import { useEntitySettings } from '../../../contexts/EntitySettingsContext';

const SEVERITIES: ImpactSeverity[] = ['Low', 'Medium', 'High', 'Critical'];

export interface ImpactDetailModalProps {
    isOpen: boolean;
    investigationId: number;
    impact: CapaImpact | null;
    frozen: boolean;
    categories: { value: number; label: string }[];
    onClose: () => void;
    onChanged: () => void;
}

const ImpactDetailModal: React.FC<ImpactDetailModalProps> = ({
    isOpen,
    investigationId,
    impact,
    frozen,
    categories,
    onClose,
    onChanged,
}) => {
    const { state } = useAuth();
    const user = state.user;
    const { formatCurrency } = useEntitySettings();

    const isOwner = !!user?.id && user.id === impact?.created_by;
    const editable = isOwner && !frozen;

    const [form, setForm] = useState<{
        impact_category_id: number | null;
        impact_description: string;
        impact_severity: ImpactSeverity;
        impact_amount: string;
    }>({
        impact_category_id: null,
        impact_description: '',
        impact_severity: 'Medium',
        impact_amount: '',
    });
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const fieldVh = useFieldValidation();

    useEffect(() => {
        if (!impact) return;
        setForm({
            impact_category_id: impact.impact_category_id ?? null,
            impact_description: impact.impact_description ?? '',
            impact_severity: impact.impact_severity ?? 'Medium',
            impact_amount: impact.impact_amount != null ? String(impact.impact_amount) : '',
        });
        fieldVh.reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [impact?.id]);

    const handleSave = async () => {
        if (!impact) return;

        if (!form.impact_category_id) {
            fieldVh.setError('impact_category_id', 'Category is required');
            return;
        }
        fieldVh.clearError('impact_category_id');

        if (!form.impact_severity) {
            fieldVh.setError('impact_severity', 'Severity is required');
            return;
        }
        fieldVh.clearError('impact_severity');

        const valid = fieldVh.validateAll([
            { name: 'impact_description', code: 110, value: form.impact_description },
        ]);
        if (!valid) return;

        setSubmitting(true);
        try {
            const data: ImpactUpdate = {
                impact_category_id: form.impact_category_id ?? undefined,
                impact_description: form.impact_description || undefined,
                impact_amount: form.impact_amount ? Number(form.impact_amount) : null,
                impact_severity: form.impact_severity,
            };
            await capaImpactsService.update(investigationId, impact.id, data);
            // No success toast: onClose() unmounts this modal immediately, so its
            // own <Toast> would never render. The list reload + close are the cue.
            onChanged();
            onClose();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to update impact') });
        } finally {
            setSubmitting(false);
        }
    };

    const headerTitle = `Impact — ${impact?.impact_category_name ?? 'Detail'}`;

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size="md" data-testid="MOD_impact_detail">
                <Modal.Header>{headerTitle}</Modal.Header>
                <Modal.Body>
                    {editable ? (
                        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                            <FormControl.SearchSelect
                                label="Category"
                                required
                                options={categories}
                                value={form.impact_category_id}
                                onChange={v => setForm(f => ({ ...f, impact_category_id: v ? Number(v) : null }))}
                                clearable
                                data-testid="INP_impact_detail_category"
                                error={fieldVh.errors['impact_category_id']}
                            />
                            <FormControl.SearchSelect
                                label="Severity"
                                required
                                options={SEVERITIES.map(s => ({ value: s, label: s }))}
                                value={form.impact_severity}
                                onChange={v => setForm(f => ({ ...f, impact_severity: (v as ImpactSeverity) ?? 'Medium' }))}
                                data-testid="INP_impact_detail_severity"
                                error={fieldVh.errors['impact_severity']}
                            />
                            <div className="col-span-2">
                                <ValidatedTextarea
                                    vh={fieldVh}
                                    name="impact_description"
                                    code={110}
                                    label="Description"
                                    value={form.impact_description}
                                    onChange={e => setForm(f => ({ ...f, impact_description: e.target.value }))}
                                    placeholder="Describe how this issue manifests as an impact"
                                    data-testid="INP_impact_detail_description"
                                />
                            </div>
                            <FormControl.Input
                                label="Financial Amount (optional)"
                                type="number"
                                value={form.impact_amount}
                                onChange={e => setForm(f => ({ ...f, impact_amount: e.target.value }))}
                                placeholder="0"
                                data-testid="INP_impact_detail_amount"
                            />
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                            {!isOwner && (
                                <div className="col-span-2">
                                    <span className="table-list-cell-sub" style={{ fontSize: '0.8rem' }}>
                                        View only — you are not the owner of this impact.
                                    </span>
                                </div>
                            )}
                            {isOwner && frozen && (
                                <div className="col-span-2">
                                    <span className="table-list-cell-sub" style={{ fontSize: '0.8rem' }}>
                                        View only — investigation is locked or closed.
                                    </span>
                                </div>
                            )}
                            <div>
                                <div className="form-label">Category</div>
                                <div data-testid="INP_impact_detail_category">
                                    {impact?.impact_category_name ?? '—'}
                                </div>
                            </div>
                            <div>
                                <div className="form-label">Severity</div>
                                <div data-testid="INP_impact_detail_severity">
                                    {impact?.impact_severity ?? '—'}
                                </div>
                            </div>
                            <div className="col-span-2">
                                <div className="form-label">Description</div>
                                <div data-testid="INP_impact_detail_description">
                                    {impact?.impact_description ?? '—'}
                                </div>
                            </div>
                            <div>
                                <div className="form-label">Financial Amount</div>
                                <div data-testid="INP_impact_detail_amount">
                                    {impact?.impact_amount != null ? formatCurrency(impact.impact_amount) : '—'}
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                {editable && (
                    <Modal.Footer>
                        <Button
                            variant="fill"
                            icon={Save}
                            disabled={submitting}
                            onClick={handleSave}
                            data-testid="BTN_impact_detail_save"
                        >
                            {submitting ? 'Saving...' : 'Save'}
                        </Button>
                    </Modal.Footer>
                )}
            </Modal>
            <Toast toast={toast} onClose={() => setToast(null)} />
        </>
    );
};

export default ImpactDetailModal;
